/**
 * Manager figures of the pilot: the pilot summary per rep, the customer-matching
 * figures per area, and the margin threshold per area.
 *
 * All three are reads over the app database. The pilot and the customer-matching
 * figures need the `see_pilot` capability, the thresholds `see_margin`; the server is
 * the gate and answers 403 otherwise. A rep never sees these cards.
 *
 * Kept free of React so the request shapes and the error wording can be tested.
 */

import { type ApiError, client } from "./client";
import type { CustomerMatchingMetrics, MarginThreshold, PilotMetrics } from "./live-types";

/** The area the pilot starts with. */
export const PILOT_DEFAULT_AREA = "dk";

/** The window the customer-matching route accepts. */
export const MATCHING_MIN_DAYS = 7;
export const MATCHING_MAX_DAYS = 120;

export interface PilotWindow {
  area: string;
  /** `YYYY-MM-DD`, inclusive. */
  from?: string;
  /** `YYYY-MM-DD`, inclusive. */
  to?: string;
}

/** `days` clamped to what the server accepts. */
export function matchingDays(days: number): number {
  if (!Number.isFinite(days)) {
    return 30;
  }
  return Math.min(MATCHING_MAX_DAYS, Math.max(MATCHING_MIN_DAYS, Math.round(days)));
}

/** `YYYY-MM-DD` (UTC) of `days` days before `now`, and of `now`. */
export function windowDates(days: number, now: Date = new Date()): { from: string; to: string } {
  const to = now.toISOString().slice(0, 10);
  const from = new Date(now.getTime() - days * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

function pilotQuery(window: PilotWindow): Record<string, string> {
  const query: Record<string, string> = { area: window.area };
  if (window.from) {
    query.from = window.from;
  }
  if (window.to) {
    query.to = window.to;
  }
  return query;
}

/** The pilot summary of one area. */
export async function getPilotMetrics(window: PilotWindow): Promise<PilotMetrics> {
  const { data } = await client.get<{ 200: PilotMetrics }, unknown, true>({
    url: "/metrics/pilot",
    query: pilotQuery(window),
    throwOnError: true,
  });
  return data ?? {};
}

/** The link of the CSV export (one row per case and rep), same origin as every API call. */
export function pilotExportUrl(window: PilotWindow): string {
  const params = new URLSearchParams(pilotQuery(window));
  return `/api/metrics/pilot/export?${params.toString()}`;
}

/**
 * The customer-matching figures of the caller's areas (or one area): the last `days`, or
 * the days `from` to `to` (both inclusive) when both are given.
 */
export async function getCustomerMatchingMetrics(options: {
  area?: string | null;
  days: number;
  from?: string;
  to?: string;
}): Promise<CustomerMatchingMetrics> {
  const query: Record<string, string | number> =
    options.from && options.to
      ? { from: options.from, to: options.to }
      : { days: matchingDays(options.days) };
  if (options.area) {
    query.area = options.area;
  }
  const { data } = await client.get<{ 200: CustomerMatchingMetrics }, unknown, true>({
    url: "/metrics/customer-matching",
    query,
    throwOnError: true,
  });
  return data ?? {};
}

/** The margin threshold of each of the caller's areas. */
export async function getMarginThresholds(): Promise<MarginThreshold[]> {
  const { data } = await client.get<{ 200: MarginThreshold[] }, unknown, true>({
    url: "/settings/margin-thresholds",
    throwOnError: true,
  });
  return Array.isArray(data)
    ? data.filter(
        (row) =>
          row != null &&
          typeof row.area === "string" &&
          typeof row.threshold_pct === "number" &&
          Number.isFinite(row.threshold_pct),
      )
    : [];
}

/** What a failed manager read reads as. */
export function metricsFailureText(error: unknown): string {
  const { status } = (error ?? {}) as Partial<ApiError>;
  if (status === 403) {
    return "Your role does not show these figures.";
  }
  if (status === 404) {
    return "These figures are not available yet.";
  }
  if (status === 503) {
    return "The figures are not reachable right now. Try again later.";
  }
  return "The figures could not be read. Try again in a moment.";
}

/** "12 min", "3.5 h", "2.0 days"; "-" when unknown. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (typeof minutes !== "number" || !Number.isFinite(minutes) || minutes < 0) {
    return "-";
  }
  if (minutes < 90) {
    return `${Math.round(minutes)} min`;
  }
  const hours = minutes / 60;
  if (hours < 36) {
    return `${hours.toFixed(1)} h`;
  }
  return `${(hours / 24).toFixed(1)} days`;
}

/** Right out of right plus wrong, as a whole percentage; null without any. */
export function precisionPct(right: number | undefined, wrong: number | undefined): number | null {
  const r = right ?? 0;
  const w = wrong ?? 0;
  return r + w === 0 ? null : Math.round((r / (r + w)) * 100);
}

/** A threshold for the table: "40 %"; the server sends a percentage (40), not a ratio. */
export function formatThreshold(pct: number): string {
  const rounded = Math.round(pct * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} %`;
}
